// =============================================================================
// MyProfile — /my-profile — the player's own profile editor (S100 Build 2)
// =============================================================================
// 🔴 RECRUIT-DIRECT VOICE, EVERYWHERE. The person reading this page is the kid:
// "you", "your film", "your numbers" — never "the recruit", never Front Office
// vocabulary. No staff chrome — one masthead, one sign-out.
//
// Data path: GET/PATCH /api/me/profile and POST /api/me/photo — the server
// resolves WHO from the JWT; this page never sends a recruit id anywhere.
// The staff-owned facts (name, school, measurables the staff verify) arrive as
// a read-only block and render as such.

import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import FieldGroup from '../components/FieldGroup.jsx';
import UrlListEditor from '../components/UrlListEditor.jsx';
import PhotoUploader from '../components/PhotoUploader.jsx';
import TranscriptUploader from '../components/TranscriptUploader.jsx';
import TemplateGallery from '../components/TemplateGallery.jsx';

// Same coerce shape the staff editor uses — '' -> null, numbers parsed,
// booleans pass through. The server re-coerces regardless; this just keeps
// what the kid sees consistent with what lands in the row.
const NUMBER_FIELDS = new Set([
  'height_inches', 'weight_lbs', 'gpa', 'sat_score', 'act_score',
  'jersey_number', 'forty_time', 'ten_yard_split', 'twenty_yard_shuttle', 'three_cone',
  'vertical_jump_inches', 'broad_jump_inches',
  'bench_press_lbs', 'bench_press_reps', 'squat_lbs', 'power_clean_lbs', 'deadlift_lbs',
]);

function coerce(field, value) {
  if (field === 'forty_time_is_laser') return !!value;
  if (NUMBER_FIELDS.has(field)) {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return value === '' ? null : value;
}

function displayValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return String(value);
  return value;
}

// The public URL line (S103). Selectable text plus a copy icon; a clipboard
// refusal is fine because the URL is selectable text either way. When the
// page is not live, say so in the kid's voice — the coach flips the switch.
function PublicUrlLine({ slug, publishedAt }) {
  const [copied, setCopied] = useState(false);
  const url = publishedAt && slug ? `https://player.ccfrecruiting.com/${slug}` : null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* refused — the text beside the button is selectable */ }
  }

  if (!url) {
    return (
      <p className="text-sm text-silver-mute -mt-2">
        Your page isn&apos;t live yet. Your CCF coach turns it on.
      </p>
    );
  }
  return (
    <div className="-mt-2 flex items-center gap-2 flex-wrap text-sm">
      <span className="text-silver-mute">Your Player Profile Link:</span>
      <span className="font-mono text-hunter select-all break-all">{url}</span>
      <button
        type="button"
        onClick={copy}
        title="Copy your page link"
        aria-label="Copy your page link"
        className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded border border-gray-300 text-hunter hover:border-mid-green"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [recruit, setRecruit] = useState(null);
  // S100 Build 3.1 — the look options come FROM the server registry, so a new
  // template appears here the moment it is registered. Never hardcoded.
  const [templates, setTemplates] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);
  const timers = useRef({});

  useEffect(() => {
    api.get('/api/me/profile')
      .then((body) => { setProfile(body.profile); setRecruit(body.recruit); setTemplates(body.templates || []); })
      .catch((err) => setLoadError(err.body?.error || err.message));
  }, []);

  // Per-field debounce, PATCHing only the field that changed — the same
  // autosave rhythm the staff editor has, sized down to one page.
  function update(field, value, owner = 'profile') {
    if (owner === 'recruit') setRecruit((prev) => ({ ...prev, [field]: value }));
    else setProfile((prev) => ({ ...prev, [field]: value }));
    clearTimeout(timers.current[field]);
    timers.current[field] = setTimeout(async () => {
      setSaveState('saving');
      try {
        await api.patch('/api/me/profile', { [field]: coerce(field, value) });
        setSaveState('saved');
        setSaveError(null);
      } catch (err) {
        setSaveState('error');
        setSaveError(err.body?.error || err.message);
      }
    }, 700);
  }

  const pv = (f) => displayValue(profile?.[f]);
  const rv = (f) => displayValue(recruit?.[f]);

  return (
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {loadError && (
          <div className="card text-sm text-red-700">{loadError}</div>
        )}

        {!profile && !loadError && (
          <div className="text-center text-silver-mute py-12">Loading your profile…</div>
        )}

        {profile && (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h1 className="font-display font-black text-2xl text-silver-text">
                {recruit ? `${recruit.first_name}, this is your page.` : 'Your page.'}
              </h1>
              <span className="text-xs text-silver-mute">
                {saveState === 'saving' ? 'Saving…'
                  : saveState === 'saved' ? 'Saved'
                    : saveState === 'error' ? (saveError || 'Could not save — try again') : ''}
              </span>
            </div>
            <p className="text-sm text-silver-mute -mt-4">
              What you put here is what college coaches will see on your profile. Keep it current.
            </p>

            {/* S103 — the kid's own public URL. The server owns slug and
                published_at (both ride on the player_profiles row); this
                block only reports and copies. Recruit-direct voice. */}
            <PublicUrlLine slug={profile.slug} publishedAt={profile.published_at} />

            {/* Pick your look — S100 Build 3.3. Live mini-renders of the REAL
                page, one per registered template. Selecting highlights; the
                button commits. Recruit-direct voice throughout. */}
            <TemplateGallery
              templates={templates}
              value={profile.metadata?.template || templates[0]?.key}
              previewUrl="/api/me/profile/preview"
              heading="Pick your look"
              blurb="This is the style of your public page — the one coaches see. Tap one to try it, then use it."
              onApply={async (key) => {
                setSaveState('saving');
                try {
                  await api.patch('/api/me/profile', { template: key });
                  setProfile((prev) => ({ ...prev, metadata: { ...(prev.metadata || {}), template: key } }));
                  setSaveState('saved');
                  setSaveError(null);
                } catch (err) {
                  setSaveState('error');
                  setSaveError(err.body?.error || err.message);
                  throw err;
                }
              }}
            />

            {/* Photo */}
            <section className="card space-y-3">
              <h2 className="font-display font-bold text-hunter">Your photo</h2>
              <PhotoUploader
                endpoint="/api/me/photo"
                url={profile.profile_photo_url}
                onUploaded={(url) => setProfile((prev) => ({ ...prev, profile_photo_url: url }))}
              />
            </section>

            {/* Identity block — read-only, honestly labeled. Measurements and
                academics live in editable fields below at Jake's 9-8 ruling. */}
            {recruit && (
              <section className="card space-y-2">
                <h2 className="font-display font-bold text-hunter">Your info from CCF</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-silver-mute">Name</span><div>{recruit.first_name} {recruit.last_name}</div></div>
                  <div><span className="text-silver-mute">Class of</span><div>{recruit.graduation_year || '—'}</div></div>
                  <div><span className="text-silver-mute">Position</span><div>{recruit.primary_position || '—'}</div></div>
                  <div><span className="text-silver-mute">School</span><div>{recruit.current_school || '—'}</div></div>
                  <div><span className="text-silver-mute">State</span><div>{recruit.state || '—'}</div></div>
                  <div><span className="text-silver-mute">NCAA ID</span><div>{recruit.ncaa_id || '—'}</div></div>
                </div>
                <p className="text-xs text-silver-mute">
                  Your CCF coach keeps this part up to date. If something here is wrong, tell them.
                </p>
              </section>
            )}

            <section className="card space-y-3">
              <h2 className="font-display font-bold text-hunter">Your transcript</h2>
              <p className="text-xs text-silver-mute">
                Upload the PDF you want college coaches to open from the Academics section of your public profile.
              </p>
              <TranscriptUploader
                endpoint="/api/me/transcript"
                transcript={profile.metadata?.transcript}
                onUploaded={(transcript) => {
                  setProfile((prev) => ({
                    ...prev,
                    metadata: { ...(prev.metadata || {}), transcript },
                  }));
                }}
              />
            </section>

            {/* About you */}
            <section className="card space-y-4">
              <h2 className="font-display font-bold text-hunter">About you</h2>
              <FieldGroup label="Bio" name="bio" value={pv('bio')} onChange={(v) => update('bio', v)} type="textarea" rows={3}
                helpText="A few sentences about who you are and how you play." />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Intended Major" name="intended_major" value={pv('intended_major')} onChange={(v) => update('intended_major', v)} />
                <FieldGroup label="Jersey Number" name="jersey_number" value={pv('jersey_number')} onChange={(v) => update('jersey_number', v)} type="number" />
                <FieldGroup label="Twitter/X Handle" name="twitter_handle" value={pv('twitter_handle')} onChange={(v) => update('twitter_handle', v)} />
                <FieldGroup label="Instagram Handle" name="instagram_handle" value={pv('instagram_handle')} onChange={(v) => update('instagram_handle', v)} />
                <FieldGroup label="TikTok Handle" name="tiktok_handle" value={pv('tiktok_handle')} onChange={(v) => update('tiktok_handle', v)} />
              </div>
            </section>

            {/* Film */}
            <section className="card space-y-4">
              <h2 className="font-display font-bold text-hunter">Your film</h2>
              <FieldGroup label="Hudl URL" name="hudl_url" value={pv('hudl_url')} onChange={(v) => update('hudl_url', v)} type="url"
                helpText="Your Hudl profile link." />
              <UrlListEditor
                label="Highlight Video URLs"
                value={Array.isArray(profile.highlight_video_urls) ? profile.highlight_video_urls : []}
                onChange={(v) => update('highlight_video_urls', v)}
              />
            </section>

            {/* Numbers */}
            <section className="card space-y-4">
              <h2 className="font-display font-bold text-hunter">Your numbers</h2>
              <p className="text-xs text-silver-mute -mt-2">
                Only put down what you can back up — coaches check.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <FieldGroup label="Height (inches)" name="height_inches" value={rv('height_inches')} onChange={(v) => update('height_inches', v, 'recruit')} type="number" />
                <FieldGroup label="Weight (lbs)" name="weight_lbs" value={rv('weight_lbs')} onChange={(v) => update('weight_lbs', v, 'recruit')} type="number" />
                <FieldGroup label="GPA" name="gpa" value={rv('gpa')} onChange={(v) => update('gpa', v, 'recruit')} type="number" />
                <FieldGroup label="SAT" name="sat_score" value={rv('sat_score')} onChange={(v) => update('sat_score', v, 'recruit')} type="number" />
                <FieldGroup label="ACT" name="act_score" value={rv('act_score')} onChange={(v) => update('act_score', v, 'recruit')} type="number" />
                <FieldGroup label="40-Yard Dash (s)" name="forty_time" value={pv('forty_time')} onChange={(v) => update('forty_time', v)} type="number" />
                <FieldGroup label="Laser timed?" name="forty_time_is_laser" value={!!profile.forty_time_is_laser} onChange={(v) => update('forty_time_is_laser', v)} type="checkbox" />
                <FieldGroup label="10-Yard Split" name="ten_yard_split" value={pv('ten_yard_split')} onChange={(v) => update('ten_yard_split', v)} type="number" />
                <FieldGroup label="Shuttle (5-10-5)" name="twenty_yard_shuttle" value={pv('twenty_yard_shuttle')} onChange={(v) => update('twenty_yard_shuttle', v)} type="number" />
                <FieldGroup label="3-Cone Drill" name="three_cone" value={pv('three_cone')} onChange={(v) => update('three_cone', v)} type="number" />
                <FieldGroup label="Vertical Jump (in)" name="vertical_jump_inches" value={pv('vertical_jump_inches')} onChange={(v) => update('vertical_jump_inches', v)} type="number" />
                <FieldGroup label="Broad Jump (in)" name="broad_jump_inches" value={pv('broad_jump_inches')} onChange={(v) => update('broad_jump_inches', v)} type="number" />
                <FieldGroup label="Bench Press (lbs)" name="bench_press_lbs" value={pv('bench_press_lbs')} onChange={(v) => update('bench_press_lbs', v)} type="number" />
                <FieldGroup label="Bench Press (reps)" name="bench_press_reps" value={pv('bench_press_reps')} onChange={(v) => update('bench_press_reps', v)} type="number" />
                <FieldGroup label="Squat (lbs)" name="squat_lbs" value={pv('squat_lbs')} onChange={(v) => update('squat_lbs', v)} type="number" />
                <FieldGroup label="Power Clean (lbs)" name="power_clean_lbs" value={pv('power_clean_lbs')} onChange={(v) => update('power_clean_lbs', v)} type="number" />
                <FieldGroup label="Deadlift (lbs)" name="deadlift_lbs" value={pv('deadlift_lbs')} onChange={(v) => update('deadlift_lbs', v)} type="number" />
              </div>
            </section>
          </>
        )}
      </main>
  );
}
